import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Trophy, Medal, TrendUp, TrendDown, Clock, Path, GasPump, CalendarCheck } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import ScorecardTripsDialog from './ScorecardTripsDialog';

const RANK_STYLES = {
  1: { bg: 'bg-[#FFD700]/15', border: 'border-l-[#FFD700]', badge: 'bg-[#FFD700] text-[#0A0A0A]' },
  2: { bg: 'bg-[#C0C0C0]/10', border: 'border-l-[#C0C0C0]', badge: 'bg-[#C0C0C0] text-[#0A0A0A]' },
  3: { bg: 'bg-[#CD7F32]/10', border: 'border-l-[#CD7F32]', badge: 'bg-[#CD7F32] text-white' },
};

const BAR_COLORS = ['#002FA7', '#0040D0', '#005AFF', '#3378FF', '#6699FF', '#99BBFF', '#BBCCFF', '#DDDDFF'];

export default function ScorecardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  // Phase 70 — drilldown modal state
  const [drill, setDrill] = useState(null); // { pilot:{id,name}, bucket }

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/admin/scorecard', { params: { from_date: fromDate, to_date: toDate } });
      setData(d);
    } catch {} finally { setLoading(false); }
  }, [fromDate, toDate]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const scorecards = data?.scorecards || [];
  const top = scorecards[0];
  const chartData = scorecards.slice(0, 10).map(s => ({ name: s.pilot_name.split(' ')[0], score: s.score, trips: s.trips_completed }));

  return (
    <div data-testid="scorecard-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tighter text-[#0A0A0A]">Pilot Scorecard</h1>
        <p className="text-[#666] text-sm mt-1">Weekly performance rankings &middot; {data?.from_date} to {data?.to_date}</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4 flex flex-wrap items-end gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest">From</Label>
          <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="rounded-none w-44" data-testid="scorecard-from" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">To</Label>
          <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="rounded-none w-44" data-testid="scorecard-to" />
        </div>
        <Button onClick={fetch_} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid="scorecard-apply-btn">
          Generate
        </Button>
        <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 7); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]); }} className="text-xs text-[#002FA7] underline">Last 7 days</button>
        <button onClick={() => { const d = new Date(); d.setDate(d.getDate() - 30); setFromDate(d.toISOString().split('T')[0]); setToDate(new Date().toISOString().split('T')[0]); }} className="text-xs text-[#002FA7] underline">Last 30 days</button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-[#666] text-sm">Calculating scores...</div>
      ) : scorecards.length === 0 ? (
        <div className="text-center py-16 text-[#666]">
          <Trophy size={48} className="mx-auto mb-4 text-[#E5E5E5]" />
          <p className="text-sm">No pilot data for this period</p>
        </div>
      ) : (
        <>
          {/* Top 3 Podium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {scorecards.slice(0, 3).map((s, i) => {
              const style = RANK_STYLES[i + 1] || {};
              return (
                <div key={s.pilot_id} className={`${style.bg} border border-[#E5E5E5] border-l-4 ${style.border} p-6 space-y-3`} data-testid={`podium-rank-${i+1}`}>
                  <div className="flex items-center justify-between">
                    <span className={`${style.badge} w-8 h-8 flex items-center justify-center text-sm font-black`}>{i + 1}</span>
                    {i === 0 && <Trophy size={28} weight="fill" className="text-[#FFD700]" />}
                    {i === 1 && <Medal size={28} weight="fill" className="text-[#C0C0C0]" />}
                    {i === 2 && <Medal size={28} weight="fill" className="text-[#CD7F32]" />}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold tracking-tight">{s.pilot_name}</h3>
                    <p className="text-xs text-[#666]">{s.emp_id}</p>
                  </div>
                  <p className="text-3xl font-black text-[#002FA7]">{s.score}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-1"><Path size={12} /> {s.trips_completed} trips</div>
                    <div className="flex items-center gap-1"><TrendUp size={12} className="text-[#00C853]" /> {s.on_time_pct}% on-time</div>
                    <div className="flex items-center gap-1"><Clock size={12} /> {s.avg_pickup_wait}m pickup</div>
                    <div className="flex items-center gap-1"><CalendarCheck size={12} /> {s.attendance_days}d present</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Score Bar Chart */}
          {chartData.length > 0 && (
            <div className="bg-white border border-[#E5E5E5] p-6">
              <h2 className="text-lg font-bold tracking-tight mb-4">Score Distribution</h2>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip contentStyle={{ border: '1px solid #E5E5E5', boxShadow: 'none', fontSize: 12 }} />
                  <Bar dataKey="score" name="Score" radius={[2, 2, 0, 0]}>
                    {chartData.map((_, idx) => (
                      <Cell key={idx} fill={BAR_COLORS[idx % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Full Table */}
          <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F3F3F3]">
                  <TableHead className="text-xs uppercase tracking-widest w-16">Rank</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Pilot</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Score</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Trips</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Completed</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Allocated</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">In-Live</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">On-Time %</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Avg Pickup Wait</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Avg Drop Wait</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Break Excess</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Attendance</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Fuel Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scorecards.map((s, i) => {
                  const isTop3 = s.rank <= 3;
                  return (
                    <TableRow key={s.pilot_id} className={i % 2 === 0 ? '' : 'bg-[#F3F3F3]/30'}>
                      <TableCell>
                        <span className={`inline-flex items-center justify-center w-7 h-7 text-xs font-black ${isTop3 ? (RANK_STYLES[s.rank]?.badge || '') : 'bg-[#F3F3F3] text-[#666]'}`}>
                          {s.rank}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{s.pilot_name}</span>
                          <span className="text-xs text-[#666] ml-2">{s.emp_id}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-black text-[#002FA7]">{s.score}</TableCell>
                      <TableCell className="text-right">
                        {s.trips_total > 0 ? (
                          <button
                            type="button"
                            onClick={() => setDrill({ pilot: { id: s.pilot_id, name: s.pilot_name }, bucket: 'all' })}
                            className="text-[#002FA7] hover:underline font-medium"
                            data-testid={`scorecard-trips-total-${s.pilot_id}`}
                          >
                            {s.trips_total}
                          </button>
                        ) : '0'}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.trips_completed > 0 ? (
                          <button
                            type="button"
                            onClick={() => setDrill({ pilot: { id: s.pilot_id, name: s.pilot_name }, bucket: 'completed' })}
                            className="text-[#00C853] hover:underline font-medium"
                            data-testid={`scorecard-trips-completed-${s.pilot_id}`}
                          >
                            {s.trips_completed}
                          </button>
                        ) : '0'}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.trips_allocated > 0 ? (
                          <button
                            type="button"
                            onClick={() => setDrill({ pilot: { id: s.pilot_id, name: s.pilot_name }, bucket: 'allocated' })}
                            className="text-[#FF8C00] hover:underline font-medium"
                            data-testid={`scorecard-trips-allocated-${s.pilot_id}`}
                          >
                            {s.trips_allocated}
                          </button>
                        ) : '0'}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.trips_inlive > 0 ? (
                          <button
                            type="button"
                            onClick={() => setDrill({ pilot: { id: s.pilot_id, name: s.pilot_name }, bucket: 'inlive' })}
                            className="text-[#002FA7] hover:underline font-medium animate-pulse"
                            data-testid={`scorecard-trips-inlive-${s.pilot_id}`}
                          >
                            {s.trips_inlive}
                          </button>
                        ) : '0'}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={s.on_time_pct >= 80 ? 'text-[#00C853]' : s.on_time_pct >= 50 ? 'text-[#FF8C00]' : 'text-[#FF3333]'}>{s.on_time_pct}%</span>
                      </TableCell>
                      <TableCell className="text-right">{s.avg_pickup_wait}m</TableCell>
                      <TableCell className="text-right">{s.avg_drop_wait}m</TableCell>
                      <TableCell className="text-right">
                        {s.total_break_excess > 0 ? <span className="text-[#FF3333]">{s.total_break_excess}m</span> : '0'}
                      </TableCell>
                      <TableCell className="text-right">{s.attendance_days}d</TableCell>
                      <TableCell className="text-right">{s.total_fuel_cost > 0 ? `${s.total_fuel_cost}` : '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Scoring Legend */}
          <div className="bg-white border border-[#E5E5E5] p-4">
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#666] mb-3">Scoring Formula</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-[#666]">
              <div className="flex items-center gap-2"><TrendUp size={14} className="text-[#00C853]" /> +10 per completed trip</div>
              <div className="flex items-center gap-2"><Clock size={14} className="text-[#002FA7]" /> +1 per on-time trip</div>
              <div className="flex items-center gap-2"><TrendDown size={14} className="text-[#FF8C00]" /> -0.5 per min excess break</div>
              <div className="flex items-center gap-2"><CalendarCheck size={14} className="text-[#00C853]" /> +2 per attendance day</div>
              <div className="flex items-center gap-2 col-span-2"><GasPump size={14} className="text-[#666]" /> Tap any trip count to drill into the trip list with GPS-based timing</div>
            </div>
          </div>
        </>
      )}

      {/* Phase 70 — drilldown dialog */}
      {drill && (
        <ScorecardTripsDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          pilot={drill.pilot}
          bucket={drill.bucket}
          fromDate={fromDate}
          toDate={toDate}
        />
      )}
    </div>
  );
}
